import {
  ProfanityCheckRule,
  ProfanityRuleItem,
  ProfanityRuleStatus,
} from '@/types/profanity'

export const DEFAULT_PROFANITY_MAX_SCORE = 100

export type ProfanityActiveStatuses = Record<ProfanityRuleStatus, boolean>
export type ProfanityThresholds = Record<ProfanityRuleStatus, number>

export type ProfanityEditorState = {
  activeStatuses: ProfanityActiveStatuses
  thresholds: ProfanityThresholds
}

export const PROFANITY_STATUS_CONFIG = [
  {
    status: ProfanityRuleStatus.ACCEPTED,
    label: 'Accept',
    barClassName: 'bg-green-500',
    handleClassName: 'border-green-500',
  },
  {
    status: ProfanityRuleStatus.REQUIRES_MANUAL_CHECK,
    label: 'Requires Manual Check',
    barClassName: 'bg-yellow-500',
    handleClassName: 'border-yellow-500',
  },
  {
    status: ProfanityRuleStatus.REJECTED,
    label: 'Reject',
    barClassName: 'bg-red-500',
    handleClassName: 'border-red-500',
  },
] as const

export const PROFANITY_STATUS_BADGE_CLASS: Record<string, string> = {
  [ProfanityRuleStatus.ACCEPTED]: 'bg-green-100 text-green-700',
  [ProfanityRuleStatus.REQUIRES_MANUAL_CHECK]: 'bg-yellow-100 text-yellow-700',
  [ProfanityRuleStatus.REJECTED]: 'bg-red-100 text-red-700',
}

const DEFAULT_THRESHOLDS: ProfanityThresholds = {
  [ProfanityRuleStatus.ACCEPTED]: 33,
  [ProfanityRuleStatus.REQUIRES_MANUAL_CHECK]: 66,
  [ProfanityRuleStatus.REJECTED]: 100,
}

const createDefaultActiveStatuses = (): ProfanityActiveStatuses => ({
  [ProfanityRuleStatus.ACCEPTED]: false,
  [ProfanityRuleStatus.REQUIRES_MANUAL_CHECK]: true,
  [ProfanityRuleStatus.REJECTED]: false,
})

const clampPercentage = (value: number, minimum = 0, maximum = 100) => (
  Math.min(Math.max(Math.round(value), minimum), maximum)
)

export const createDefaultProfanityEditorState = (): ProfanityEditorState => ({
  activeStatuses: createDefaultActiveStatuses(),
  thresholds: { ...DEFAULT_THRESHOLDS },
})

export const getActiveProfanityStatuses = (state: ProfanityEditorState) => (
  PROFANITY_STATUS_CONFIG
    .map(({ status }) => status)
    .filter((status) => state.activeStatuses[status])
)

export const getEffectiveProfanityThresholds = (state: ProfanityEditorState): ProfanityThresholds => {
  const effectiveThresholds: ProfanityThresholds = {
    [ProfanityRuleStatus.ACCEPTED]: clampPercentage(state.thresholds[ProfanityRuleStatus.ACCEPTED]),
    [ProfanityRuleStatus.REQUIRES_MANUAL_CHECK]: clampPercentage(
      state.thresholds[ProfanityRuleStatus.REQUIRES_MANUAL_CHECK]
    ),
    [ProfanityRuleStatus.REJECTED]: clampPercentage(state.thresholds[ProfanityRuleStatus.REJECTED]),
  }
  const activeStatuses = getActiveProfanityStatuses(state)
  let previousThreshold = 0

  activeStatuses.forEach((status, index) => {
    const isLast = index === activeStatuses.length - 1
    if (isLast) {
      effectiveThresholds[status] = DEFAULT_PROFANITY_MAX_SCORE
      return
    }

    const remainingRanges = activeStatuses.length - index - 1
    const minimum = previousThreshold + 1
    const maximum = DEFAULT_PROFANITY_MAX_SCORE - remainingRanges
    const threshold = clampPercentage(state.thresholds[status], minimum, maximum)
    effectiveThresholds[status] = threshold
    previousThreshold = threshold
  })

  return effectiveThresholds
}

export const mapProfanityEditorStateToRules = (state: ProfanityEditorState): ProfanityRuleItem[] => {
  const effectiveThresholds = getEffectiveProfanityThresholds(state)
  let previousThreshold = 0

  return getActiveProfanityStatuses(state).map((status) => {
    const rule = {
      min_threshold: previousThreshold,
      max_threshold: effectiveThresholds[status],
      rule_status: status,
    }
    previousThreshold = rule.max_threshold
    return rule
  })
}

export const mapProfanityPolicyToEditorState = (
  policy: ProfanityCheckRule | null
): ProfanityEditorState => {
  if (!policy?.rules?.length) return createDefaultProfanityEditorState()

  const state: ProfanityEditorState = {
    activeStatuses: {
      [ProfanityRuleStatus.ACCEPTED]: false,
      [ProfanityRuleStatus.REQUIRES_MANUAL_CHECK]: false,
      [ProfanityRuleStatus.REJECTED]: false,
    },
    thresholds: { ...DEFAULT_THRESHOLDS },
  }
  const policyMaxScore = policy.max_score > 0 ? policy.max_score : DEFAULT_PROFANITY_MAX_SCORE

  policy.rules.forEach((rule) => {
    if (!Object.prototype.hasOwnProperty.call(state.activeStatuses, rule.rule_status)) return
    state.activeStatuses[rule.rule_status] = true
    if (rule.max_threshold < policyMaxScore) {
      state.thresholds[rule.rule_status] = clampPercentage(
        (rule.max_threshold / policyMaxScore) * DEFAULT_PROFANITY_MAX_SCORE
      )
    }
  })

  if (getActiveProfanityStatuses(state).length === 0) {
    return createDefaultProfanityEditorState()
  }

  return state
}

export const updateProfanityThreshold = (
  state: ProfanityEditorState,
  status: ProfanityRuleStatus,
  value: number
) => {
  const nextState: ProfanityEditorState = {
    ...state,
    thresholds: {
      ...state.thresholds,
      [status]: value,
    },
  }
  const committedValue = getEffectiveProfanityThresholds(nextState)[status]
  nextState.thresholds[status] = committedValue

  return { state: nextState, committedValue }
}
